/**
 * 管理后台 - 纠错管理页面
 * 
 * 功能:
 * 1. 统一管理学员提交的纠错记录
 * 2. 审核通过/拒绝
 * 3. 自定义奖励U币（默认50分）
 * 4. 发送站内信通知
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { extractBeijingTime } from '../../utils/timeUtils';
import { supabase, getSupabaseUrl } from '../../supabase/client';
import CorrectionDetailModal from '../../components/CorrectionDetailModal';

interface Correction {
  id: string;
  submitter_id: string;
  submitter_role: string;
  target_type: string;
  target_id: string;
  error_description: string;
  suggested_correction: string | null;
  evidence_images: string[] | null;
  severity: string;
  status: string;
  reviewer_id: string | null;
  review_comment: string | null;
  reviewed_at: string | null;
  reward_points: number | null;
  created_at: string;
  submitter_username?: string;

  // 扩展字段
  question_content?: string | null;
  question_options?: any | null;
  paper_title?: string | null;
  error_type?: string | null;
  source?: string;
  resolution_note?: string | null;

  // 新增：题目信息（用于显示纠错对象）
  question_title?: string | null;
  question_type?: string | null;
  question_paper_id?: string | null;
  question_order_index?: number | null;

  // 新增：题目详细信息（用于详情弹窗展示）
  reference_answer?: string | null;
  explanation?: string | null;
  scoring_points?: any | null;
  options?: any | null;
  title_images?: string[] | null;
  title_image_widths?: number[] | null;
  title_image_layout?: string | null;
  image_position?: string | null;
  option_layout?: string | null;
}

const TARGET_TYPE_LABELS: Record<string, string> = {
  question: '题目',
  paper: '试卷',
  answer: '答案',
  explanation: '解析',
};

const SEVERITY_LABELS: Record<string, string> = {
  low: '低',
  medium: '中',
  high: '高',
  critical: '严重',
};

const STATUS_LABELS: Record<string, string> = {
  pending: '待审核',
  reviewing: '审核中',
  approved: '已通过',
  rejected: '已拒绝',
  processed: '已处理',
};

const ERROR_TYPE_LABELS: Record<string, string> = {
  answer_error: '答案错误',
  question_error: '题目错误',
  option_error: '选项错误',
  explanation_error: '解析错误',
  other: '其他问题',
};

const QUESTION_TYPE_MAP: Record<string, string> = {
  single_choice: '单选题',
  multi_choice: '多选题',
  multiple_choice: '多选题',
  judge: '判断题',
  subjective: '主观题',
  coding: '编程题',
  python: 'Python编程',
  python_coding: 'Python编程',
  cpp: 'C++编程',
  cpp_coding: 'C++编程',
  graphic: '图形化编程',
  graphics: '图形化编程',
  scratch_coding: '图形化编程',
};

const getQuestionTypeName = (type: string): string => {
  return QUESTION_TYPE_MAP[type] || type;
};

// 判断是否为客观题
const isObjectiveQuestion = (questionType: string | null | undefined): boolean => {
  if (!questionType) return false;
  const objectiveTypes = ['single_choice', 'multi_choice', 'multiple_choice', 'judge'];
  return objectiveTypes.includes(questionType);
};

// 获取题型中文名称
const getQuestionTypeLabel = (questionType: string | null | undefined): string => {
  const typeMap: Record<string, string> = {
    'single_choice': '单选题',
    'multi_choice': '多选题',
    'multiple_choice': '多选题',
    'judge': '判断题',
    'python_coding': 'Python编程',
    'cpp_coding': 'C++编程',
    'scratch_coding': 'Scratch编程',
    'graphic': '图形化编程',
  };
  return typeMap[questionType || ''] || '题目';
};

// 去除HTML标签，只保留纯文本
const stripHtmlTags = (html: string): string => {
  if (!html) return '';
  // 创建一个临时div元素来解析HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  return tempDiv.textContent || tempDiv.innerText || '';
};

// 格式化纠错对象显示
const formatCorrectionTarget = (correction: Correction): string => {
  const paperName = correction.paper_title || '未命名试卷';

  if (correction.target_type === 'question') {
    const questionType = correction.question_type;
    const questionTitle = correction.question_title || '';
    const orderIndex = correction.question_order_index;
    const typeLabel = getQuestionTypeLabel(questionType);

    // 统一格式：【完整试卷名称】+【题型】+【题号】+【题目名称】
    const parts = [paperName, typeLabel];
    if (orderIndex != null) {
      parts.push(`第${orderIndex}题`);
    }
    if (questionTitle) {
      parts.push(questionTitle);
    }

    return `【${parts.join(' ')}】`;
  }

  // 其他类型（试卷、答案等）
  return `${TARGET_TYPE_LABELS[correction.target_type] || '题目'}：【${paperName}】`;
};

const ErrorCorrectionReview: React.FC = () => {
  const navigate = useNavigate();
  const [corrections, setCorrections] = useState<Correction[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [filterErrorType, setFilterErrorType] = useState<string>('all');
  const [selectedCorrection, setSelectedCorrection] = useState<Correction | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    fetchCorrections();
  }, [filterStatus, filterErrorType]);

  const fetchCorrections = async () => {
    try {
      setLoading(true);

      let query = supabase
        .from('error_corrections')
        .select('*')
        .order('created_at', { ascending: false });

      if (filterStatus !== 'all') {
        query = query.eq('status', filterStatus);
      }

      if (filterErrorType !== 'all') {
        query = query.eq('error_type', filterErrorType);
      }

      const { data } = await query;

      if (data) {
        // 获取提交人用户名
        const userIds = [...new Set(data.map(c => c.submitter_id))];
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, username')
          .in('id', userIds);

        const profileMap = new Map((profiles || []).map(p => [p.id, p.username]));

        // 获取题目信息（用于显示纠错对象）
        const questionIds = [...new Set(data.filter(c => c.target_type === 'question' && c.target_id).map(c => c.target_id))];
        let questionMap = new Map();
        let paperMap = new Map();

        if (questionIds.length > 0) {
          const { data: questions } = await supabase
            .from('questions')
            .select('id, title, question_type, content, paper_id, order_index, reference_answer, explanation, scoring_points, options, title_images, title_image_widths, title_image_layout, image_position, option_layout')
            .in('id', questionIds);

          questionMap = new Map((questions || []).map(q => [q.id, q]));

          // 通过 paper_id 关联查询试卷名称
          const paperIds = [...new Set((questions || []).filter(q => q.paper_id).map(q => q.paper_id))];
          if (paperIds.length > 0) {
            const { data: papers } = await supabase
              .from('papers')
              .select('id, title')
              .in('id', paperIds);

            paperMap = new Map((papers || []).map(p => [p.id, p.title]));
          }
        }

        const correctionsWithInfo = data.map(c => {
          const question = c.target_type === 'question' ? questionMap.get(c.target_id) : null;
          // 优先使用从 papers 表查询的真实试卷名称，其次使用 error_corrections 表中的 paper_title
          const realPaperTitle = question?.paper_id ? paperMap.get(question.paper_id) : null;
          return {
            ...c,
            submitter_username: profileMap.get(c.submitter_id) || '未知用户',
            paper_title: realPaperTitle || c.paper_title || '未命名试卷',
            question_title: question?.title || null,
            question_type: question?.question_type || null,
            question_content: question?.content || null,
            question_paper_id: question?.paper_id || null,
            question_order_index: question?.order_index || null,
            reference_answer: question?.reference_answer || null,
            explanation: question?.explanation || null,
            scoring_points: question?.scoring_points || null,
            question_options: question?.options || null,
            title_images: question?.title_images || null,
            title_image_widths: question?.title_image_widths || null,
            title_image_layout: question?.title_image_layout || null,
            image_position: question?.image_position || null,
            option_layout: question?.option_layout || null,
          };
        });

        setCorrections(correctionsWithInfo);
      }
    } catch (error) {
      console.error('Failed to fetch corrections:', error);
    } finally {
      setLoading(false);
    }
  };

  const grantReward = async (correction: Correction, customPoints?: number) => {
    try {
      const points = customPoints || 50;

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/point-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'add_points',
          user_id: correction.submitter_id,
          role: correction.submitter_role,
          points: points,
          point_type: 'error_correction',
          source_type: 'correction',
          source_id: correction.id,
          remark: `纠错奖励（${correction.paper_title || '未命名试卷'} - ${getQuestionTypeLabel(correction.question_type)}${correction.question_order_index ? ` - 第${correction.question_order_index}题` : ''}）`,
        }),
      });

      const result = await response.json();
      if (!result.success) {
        console.error('Failed to grant reward:', result.error);
      }
    } catch (error) {
      console.error('Failed to grant reward:', error);
    }
  };

  const sendNotification = async (correction: Correction, approved: boolean, comment?: string) => {
    try {
      const statusLabel = approved ? '已通过' : '已拒绝';
      const title = `纠错反馈${statusLabel}`;

      // 构建题目信息
      const paperName = correction.paper_title || '未命名试卷';
      const questionType = correction.error_type ? ERROR_TYPE_LABELS[correction.error_type] || '未知类型' : '未知类型';

      // 构建 HTML 内容
      let content = `<div style="font-size: 14px; line-height: 1.6;">`;
      content += `<div style="margin-bottom: 12px; padding: 12px; background: #f0f7ff; border-radius: 8px; border-left: 4px solid #3b82f6;">`;
      content += `<div style="font-weight: bold; color: #1f2937; margin-bottom: 8px;">${paperName}</div>`;
      content += `<div style="color: #6b7280;">错误类型：${questionType}</div>`;
      content += `</div>`;
      content += `<div style="margin-bottom: 12px;"><strong>您的反馈：</strong>${correction.error_description}</div>`;
      content += `<div style="padding: 12px; background: ${approved ? '#f0fdf4' : '#fef2f2'}; border-radius: 8px; border-left: 4px solid ${approved ? '#22c55e' : '#ef4444'};">`;
      content += `<div style="font-weight: bold; color: #1f2937;">处理结果：${statusLabel}</div>`;
      if (comment) {
        content += `<div style="color: #6b7280; margin-top: 8px;">审核意见：${comment}</div>`;
      }
      content += `</div></div>`;

      // 调用消息服务发送站内信
      await fetch(`${getSupabaseUrl()}/functions/v1/message-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'messages_send_notification',
          userIds: [correction.submitter_id],
          title,
          content,
          category: 'system',
          priority: 'normal',
        }),
      });
    } catch (error) {
      console.error('Failed to send notification:', error);
    }
  };

  const openDetailModal = (correction: Correction) => {
    setSelectedCorrection(correction);
    setShowDetailModal(true);
  };

  // 审核通过处理
  const handleApprove = async (rewardPoints: number, comment: string) => {
    if (!selectedCorrection) return;

    try {
      // 更新数据库
      const updateData: any = {
        status: 'approved',
        reviewer_id: (await supabase.auth.getUser()).data.user?.id,
        review_comment: comment || null,
        reviewed_at: new Date().toISOString(),
        reward_points: rewardPoints,
      };

      const { error } = await supabase
        .from('error_corrections')
        .update(updateData)
        .eq('id', selectedCorrection.id);

      if (error) throw error;

      // 发放奖励U币
      await grantReward(selectedCorrection, rewardPoints);

      // 发送通知
      await sendNotification(selectedCorrection, true, comment);

      alert(`审核通过，已发放 ${rewardPoints} U币奖励并发送通知`);
      fetchCorrections();
    } catch (error) {
      console.error('Failed to approve:', error);
      throw error;
    }
  };

  // 审核拒绝处理
  const handleReject = async (comment: string) => {
    if (!selectedCorrection) return;

    try {
      // 更新数据库
      const updateData: any = {
        status: 'rejected',
        reviewer_id: (await supabase.auth.getUser()).data.user?.id,
        review_comment: comment || null,
        reviewed_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('error_corrections')
        .update(updateData)
        .eq('id', selectedCorrection.id);

      if (error) throw error;

      // 发送通知
      await sendNotification(selectedCorrection, false, comment);

      alert('已拒绝并发送通知');
      fetchCorrections();
    } catch (error) {
      console.error('Failed to reject:', error);
      throw error;
    }
  };

  const formatDate = (dateStr: string) => {
    const bj = extractBeijingTime(dateStr);
    if (!bj) return '-';
    return `${bj.year}-${bj.month}-${bj.day} ${bj.hour}:${bj.minute}`;
  };

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">纠错管理</h1>
        <p className="text-sm text-gray-500 mt-1">管理学员提交的纠错反馈</p>
      </div>

      {/* 筛选器 */}
      <div className="bg-white rounded-xl p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">状态:</span>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="pending">待审核</option>
              <option value="reviewing">审核中</option>
              <option value="approved">已通过</option>
              <option value="rejected">已拒绝</option>
              <option value="processed">已处理</option>
              <option value="all">全部</option>
            </select>
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">错误类型:</span>
            <select
              value={filterErrorType}
              onChange={(e) => setFilterErrorType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="all">全部</option>
              <option value="answer_error">答案错误</option>
              <option value="question_error">题目错误</option>
              <option value="option_error">选项错误</option>
              <option value="explanation_error">解析错误</option>
              <option value="other">其他问题</option>
            </select>
          </div>
        </div>
      </div>

      {/* 列表 */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">加载中...</div>
      ) : corrections.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">📝</div>
          <div className="text-gray-500">暂无纠错记录</div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-3 px-4 font-medium text-gray-700">提交人</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">纠错对象</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">错误类型</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">严重程度</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">状态</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">提交时间</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">操作</th>
              </tr>
            </thead>
            <tbody>
              {corrections.map((correction) => (
                <tr key={correction.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4">
                    <button
                      onClick={() => navigate(`/admin/members?userId=${correction.submitter_id}`)}
                      className="text-blue-600 hover:text-blue-800 hover:underline text-sm"
                      title="点击查看学员详情"
                    >
                      {correction.submitter_username}
                    </button>
                  </td>
                  <td className="py-3 px-4">
                    <div className="text-sm text-gray-700">{formatCorrectionTarget(correction)}</div>
                  </td>
                  <td className="py-3 px-4">
                    {correction.error_type ? (
                      <span className="text-xs text-gray-600">{ERROR_TYPE_LABELS[correction.error_type] || correction.error_type}</span>
                    ) : (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                      correction.severity === 'critical' ? 'bg-red-100 text-red-700' :
                      correction.severity === 'high' ? 'bg-orange-100 text-orange-700' :
                      correction.severity === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {SEVERITY_LABELS[correction.severity]}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                      correction.status === 'approved' ? 'bg-green-100 text-green-700' :
                      correction.status === 'rejected' ? 'bg-red-100 text-red-700' :
                      correction.status === 'processed' ? 'bg-blue-100 text-blue-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {STATUS_LABELS[correction.status]}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-gray-500">{formatDate(correction.created_at)}</td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      {correction.question_paper_id && (
                        <button
                          onClick={() => navigate(`/admin/paper-editor?edit=${correction.question_paper_id}`)}
                          className="text-purple-600 hover:text-purple-800 text-sm"
                          title="查看原卷"
                        >
                          查看原卷
                        </button>
                      )}
                      <button
                        onClick={() => openDetailModal(correction)}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        查看详情
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 纠错详情弹窗 */}
      {showDetailModal && selectedCorrection && (
        <CorrectionDetailModal
          correction={selectedCorrection}
          isOpen={showDetailModal}
          onClose={() => setShowDetailModal(false)}
          isAdminMode={true}
          onApprove={handleApprove}
          onReject={handleReject}
          defaultRewardPoints={50}
        />
      )}
    </div>
  );
};

export default ErrorCorrectionReview;
